import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import { getFileTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { sectorAt } from '@/config/sectors';
import { loadSlot, type SaveData } from '@/core/save';
import { formatStat } from '@/core/records';
import { t } from '@/i18n';
import { chamferPanel, label, targetBrackets } from './widgets';

export const SLOT_H = 64;

/** Generic title row: chamfer panel + centered label, centered at (cx, cy). */
export function rowPanel(
  text: string, selected: boolean, cx: number, cy: number,
  w: number, h: number, size: number,
): Container {
  const btn = new Container();
  const bg = new Graphics();
  chamferPanel(bg, w, h, {
    fill: selected ? 0x2a2440 : 0x161222,
    stroke: selected ? COLORS.uiAccent : 0x4a4256,
    strokeWidth: selected ? 4 : 3,
    notch: 13,
  });
  if (selected) targetBrackets(bg, w, h);
  bg.position.set(-w / 2, -h / 2);
  const txt = label(text, size, selected ? COLORS.playerYellow : 0xffffff, 3);
  txt.anchor.set(0.5);
  btn.addChild(bg, txt);
  btn.position.set(cx, cy);
  return btn;
}

/** Save-slot row: summary when occupied, action tag on the right. */
export function slotRow(
  i: number, mode: 'new' | 'continue', selected: boolean,
  cx: number, cy: number, screenW: number,
): Container {
  const save: SaveData | null = loadSlot(i);
  const w = Math.min(620, screenW - 60);
  const row = new Container();
  const bg = new Graphics();
  chamferPanel(bg, w, SLOT_H, {
    fill: selected ? 0x2a2440 : 0x161222,
    stroke: selected ? COLORS.uiAccent : save ? 0x4a4256 : 0x322c40,
    strokeWidth: selected ? 4 : 3,
    notch: 14,
  });
  if (selected) targetBrackets(bg, w, SLOT_H);
  bg.position.set(-w / 2, -SLOT_H / 2);
  row.addChild(bg);

  const name = label(t('title.slot', { n: i + 1 }), 18, selected ? COLORS.playerYellow : 0xffffff, 2);
  name.anchor.set(0, 0.5);
  name.position.set(-w / 2 + 22, save ? -12 : 0);
  row.addChild(name);

  if (save) {
    const info = label(slotSummary(save), 13, 0x9aa0a6, 1);
    info.anchor.set(0, 0.5);
    info.position.set(-w / 2 + 22, 13);
    row.addChild(info);
  }
  const actionKey = mode === 'continue'
    ? 'title.continue' as const
    : save ? 'title.overwrite' as const : 'title.slotEmpty' as const;
  const color = actionKey === 'title.overwrite'
    ? COLORS.dangerOrange
    : selected ? COLORS.uiAccent : 0x6a7076;
  const action = label(t(actionKey), 14, color, 2);
  action.anchor.set(1, 0.5);
  action.position.set(w / 2 - 22, 0);
  row.addChild(action);

  row.position.set(cx, cy);
  return row;
}

/** One-line slot summary: sector + room, or endless depth (pre-sector saves). */
function slotSummary(save: SaveData): string {
  const common = {
    room: save.roomNumber + 1,
    cards: save.ownedCardIds.length,
    time: formatStat('timeAlive', save.stats.timeAlive),
  };
  if (save.mode === 'sector') {
    const index = save.sector ?? 0;
    return t('title.slotSector', { ...common, s: index + 1, total: sectorAt(index).rooms + 1 });
  }
  return t('title.slotEndless', { ...common, runs: save.totalRuns ?? 1 });
}

/** Main-menu art: wordmark + tag + the Atlas pilot on the right side. */
export function buildTitleArt(screenW: number, screenH: number): Container {
  const c = new Container();
  const logo = new Text({
    text: 'ATLAS',
    style: new TextStyle({
      fontFamily: 'Tektur, Arial Black, sans-serif',
      fontSize: 96, fill: COLORS.uiAccent, letterSpacing: 16,
      stroke: { color: COLORS.outline, width: 8 },
    }),
  });
  logo.anchor.set(0.5);
  logo.position.set(screenW / 2, screenH * 0.19);
  const sub = new Text({
    text: 'BOSS RUSH',
    style: new TextStyle({
      fontFamily: 'Tektur, Arial Black, sans-serif',
      fontSize: 40, fill: COLORS.playerYellow, letterSpacing: 22,
      stroke: { color: COLORS.outline, width: 5 },
    }),
  });
  sub.anchor.set(0.5);
  sub.position.set(screenW / 2, screenH * 0.265);
  c.addChild(sub);
  const tag = new Text({
    text: t('title.tag'),
    style: new TextStyle({
      fontFamily: 'Tektur, Arial Black, sans-serif',
      fontSize: 18, fill: 0x9aa0a6, letterSpacing: 6,
    }),
  });
  tag.anchor.set(0.5);
  tag.position.set(screenW / 2, screenH * 0.325);
  c.addChild(logo, tag);

  // Atlas greets right BESIDE the menu, not parked at the screen edge
  const avatarTex = getFileTexture('ui/title-avatar');
  if (avatarTex) {
    const avatar = new Sprite(avatarTex);
    avatar.anchor.set(0, 1);
    avatar.scale.set((screenH * 0.72) / avatarTex.height);
    avatar.position.set(screenW / 2 + 205, screenH * 0.985);
    avatar.alpha = 0.96;
    c.addChild(avatar);
  }
  return c;
}
